/**
 * Global quick-view-drawer custom element for Amiy.
 * One drawer; opens on [data-quick-view] click, fetches product HTML and injects.
 * Uses GSAP for open/close animations when available (Maya-style).
 */
(function () {
  if (customElements.get('quick-view-drawer')) return;

  var GSAP_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function replaceIdsInHtml(html, sectionId) {
    if (!sectionId) return html;
    var safe = sectionId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return html.replace(new RegExp(safe, 'g'), 'quickview-' + sectionId);
  }

  function reinjectScripts(container) {
    if (!container) return;
    container.querySelectorAll('script').forEach(function (oldScript) {
      var newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(function (attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  customElements.define('quick-view-drawer', class QuickViewDrawer extends HTMLElement {
    constructor() {
      super();
      this.contentEl = this.querySelector('[data-quick-view-content]');
      this.backdrop = this.querySelector('[data-backdrop-overlay]');
      this.inner = this.querySelector('.overlay-drawer-inner');
      this.closeBtn = this.querySelector('[data-drawer-close]');
      this._boundClose = this.close.bind(this);
      this._boundBackdropClick = this._onBackdropClick.bind(this);
      this._gsapReady = null;
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      if (this.closeBtn) this.closeBtn.addEventListener('click', this._boundClose);
      if (this.backdrop) this.backdrop.addEventListener('click', this._boundBackdropClick);
      this.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') this.close();
      }.bind(this));
      document.addEventListener('click', this._onDocumentClick.bind(this));
    }

    _onBackdropClick(e) {
      if (e.target === this.backdrop || e.target.closest('[data-backdrop-overlay]') === this.backdrop) {
        this.close();
      }
    }

    _onDocumentClick(e) {
      var trigger = e.target.closest('[data-quick-view]');
      if (!trigger || !trigger.dataset.productUrl) return;
      e.preventDefault();
      var url = trigger.dataset.productUrl;
      if (!url) return;
      if (trigger.classList && trigger.classList.add) {
        trigger.classList.add('loading');
        if (trigger.querySelector('.ajax-loader')) trigger.querySelector('.ajax-loader').classList.remove('hidden');
      }
      this.open(url).finally(function () {
        if (trigger.classList && trigger.classList.remove) {
          trigger.classList.remove('loading');
          if (trigger.querySelector('.ajax-loader')) trigger.querySelector('.ajax-loader').classList.add('hidden');
        }
      });
    }

    _ensureGsap() {
      if (this._gsapReady !== null) return this._gsapReady;
      if (typeof gsap !== 'undefined') {
        this._gsapReady = Promise.resolve();
        return this._gsapReady;
      }
      this._gsapReady = loadScript(GSAP_CDN);
      return this._gsapReady;
    }

    _animateOpen() {
      var self = this;
      if (typeof gsap === 'undefined' || !this.inner || !this.backdrop) return;
      var media = this.inner && this.inner.querySelector('.quickview-product-media');
      var info = this.inner && this.inner.querySelector('.quickview-product-info');
      gsap.set(this.backdrop, { opacity: 0, backdropFilter: 'blur(0px)' });
      gsap.set(this.inner, { x: '100%', scale: 0.96 });
      if (media) gsap.set(media, { opacity: 0, x: -72 });
      if (info) gsap.set(info, { opacity: 0, x: 72 });
      var tl = gsap.timeline({ overwrite: true });
      tl.to(this.backdrop, {
        opacity: 1,
        backdropFilter: 'blur(10px)',
        duration: 0.45,
        ease: 'power2.out'
      }, 0);
      tl.to(this.inner, {
        x: 0,
        scale: 1,
        duration: 0.5,
        ease: 'back.out(1.12)'
      }, 0);
      tl.to(media, {
        opacity: 1,
        x: 0,
        duration: 0.45,
        ease: 'back.out(1.08)'
      }, 0.22);
      tl.to(info, {
        opacity: 1,
        x: 0,
        duration: 0.45,
        ease: 'back.out(1.08)'
      }, 0.32);
    }

    _animateClose(done) {
      var self = this;
      if (typeof gsap === 'undefined' || !this.inner || !this.backdrop) {
        if (done) done();
        return;
      }
      var media = this.inner && this.inner.querySelector('.quickview-product-media');
      var info = this.inner && this.inner.querySelector('.quickview-product-info');
      var tl = gsap.timeline({
        onComplete: function () {
          self.classList.remove('open', 'qv-use-gsap');
          self.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('overflow-hidden');
          if (done) done();
        }
      });
      tl.to(media, {
        opacity: 0,
        x: -48,
        duration: 0.28,
        ease: 'power2.in'
      }, 0);
      tl.to(info, {
        opacity: 0,
        x: 48,
        duration: 0.28,
        ease: 'power2.in'
      }, 0.06);
      tl.to(this.inner, {
        x: '110%',
        scale: 0.96,
        duration: 0.38,
        ease: 'back.in(1.1)'
      }, 0.12);
      tl.to(this.backdrop, {
        opacity: 0,
        backdropFilter: 'blur(0px)',
        duration: 0.35,
        ease: 'power2.in'
      }, 0.12);
    }

    open(productUrl) {
      var self = this;
      this.contentEl = this.querySelector('[data-quick-view-content]');
      this.inner = this.querySelector('.overlay-drawer-inner');
      this.backdrop = this.querySelector('[data-backdrop-overlay]');
      if (!this.contentEl) return Promise.reject();
      this.contentEl.innerHTML = '';
      this.classList.add('qv-use-gsap', 'open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      function runOpen() {
        self._ensureGsap().then(function () {
          requestAnimationFrame(function () { self._animateOpen(); });
        }).catch(function () {
          self.classList.remove('qv-use-gsap');
        });
      }
      return fetch(productUrl)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var section = doc.querySelector('section[id^="MainProduct-"]');
          if (!section) {
            self.contentEl.innerHTML = '<p class="p-4 text-center">Unable to load product.</p>';
            runOpen();
            return;
          }
          var sectionId = section.dataset.section || section.getAttribute('data-section');
          var inner = section.innerHTML;
          inner = replaceIdsInHtml(inner, sectionId);
          self.contentEl.innerHTML = inner;
          reinjectScripts(self.contentEl);
          if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
          runOpen();
        })
        .catch(function () {
          self.contentEl.innerHTML = '<p class="p-4 text-center">Unable to load product.</p>';
          runOpen();
        });
    }

    close() {
      var self = this;
      if (this.classList.contains('qv-use-gsap')) {
        this._animateClose();
      } else {
        this.classList.remove('open');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
      }
    }
  });
})();
